import { ADMIN_IMAGE_KEYS, ADMIN_IMAGES, ADMIN_FILES, fileBasename } from "./media";

const fieldClass =
  "mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-lightText dark:border-gray-600 dark:bg-gray-900 dark:text-darkText";
const labelClass = "block text-sm font-semibold uppercase tracking-wide text-gray-500";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className={labelClass}>{label}</span>
    {children}
  </label>
);

export const TextInput = ({ label, value, onChange, type = "text", placeholder }) => (
  <Field label={label}>
    <input
      type={type}
      className={fieldClass}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) =>
        onChange(type === "number" ? Number(e.target.value) : e.target.value)
      }
    />
  </Field>
);

export const TextArea = ({ label, value, onChange, rows = 4 }) => (
  <Field label={label}>
    <textarea
      className={`${fieldClass} font-sans`}
      rows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

export const TextCheck = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded border-gray-300 text-green dark:text-green-dark focus:ring-green"
    />
    {label}
  </label>
);

export const ChipMultiSelect = ({ label, options, values = [], onChange }) => (
  <div>
    <p className={labelClass}>{label}</p>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = values.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() =>
              onChange(
                active
                  ? values.filter((v) => v !== opt.id)
                  : [...values, opt.id],
              )
            }
            className={`rounded-full border px-2.5 py-1 text-sm transition-colors ${
              active
                ? "border-green bg-green/10 text-green dark:text-green-dark"
                : "border-gray-300 text-gray-600 hover:border-green dark:border-gray-600 dark:text-gray-400"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const ImagePicker = ({ value, onChange }) => (
  <div>
    <p className={labelClass}>Image</p>
    <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
      {ADMIN_IMAGE_KEYS.map((key) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`overflow-hidden rounded-md border p-1.5 text-left transition-colors ${
              active
                ? "border-green ring-2 ring-green/30"
                : "border-gray-200 hover:border-green/50 dark:border-gray-700"
            }`}
            title={key}
          >
            <div className="flex h-16 items-center justify-center bg-white dark:bg-gray-900">
              <img
                src={ADMIN_IMAGES[key]}
                alt={key}
                className="max-h-14 max-w-full object-contain"
              />
            </div>
            <p className="mt-1 truncate text-[10px] text-gray-500">{key}</p>
          </button>
        );
      })}
    </div>
  </div>
);

export const FilePicker = ({
  label,
  value,
  onChange,
  extraFiles = [],
  onUpload,
}) => {
  const options = [...new Set([...ADMIN_FILES, ...extraFiles, value].filter(Boolean))];

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    onUpload(file);
    e.target.value = "";
  };

  return (
    <div>
      <p className={labelClass}>{label}</p>
      <select
        className={fieldClass}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">— None —</option>
        {options.map((path) => (
          <option key={path} value={path}>
            {fileBasename(path)}
          </option>
        ))}
      </select>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="cursor-pointer rounded border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:border-green hover:text-green dark:hover:text-green-dark dark:border-gray-600 dark:text-gray-400">
          Upload / prepare file…
          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUpload} />
        </label>
        {value && (
          <a
            href={value.startsWith("http") ? value : `/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-green dark:text-green-dark underline underline-offset-2"
          >
            Open current
          </a>
        )}
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        Upload downloads a copy named for <code>public/files/</code>. Drop it there, then pick it here (or refresh the list after adding to the registry).
      </p>
    </div>
  );
};

export const StringListEditor = ({ label, values = [], onChange }) => {
  const updateAt = (index, next) => {
    const copy = [...values];
    copy[index] = next;
    onChange(copy);
  };
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <p className={labelClass}>{label}</p>
        <button
          type="button"
          className="text-sm font-medium text-green dark:text-green-dark underline underline-offset-2"
          onClick={() => onChange([...values, ""])}
        >
          + Add
        </button>
      </div>
      <div className="space-y-2">
        {values.map((item, index) => (
          <div key={index} className="flex gap-2">
            <textarea
              className={`${fieldClass} font-sans`}
              rows={3}
              value={item}
              onChange={(e) => updateAt(index, e.target.value)}
            />
            <button
              type="button"
              className="shrink-0 text-sm text-red-500 hover:underline"
              onClick={() => onChange(values.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RichHint = () => (
  <p className="mt-1 text-[11px] text-gray-500">
    HTML ok:{" "}
    <code className="text-[10px]">
      {'<a href="…" target="_blank" rel="noopener noreferrer" class="text-green dark:text-green-dark underline underline-offset-2">link</a>'}
    </code>
  </p>
);
